import React, {useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import useMeasure from 'react-use/lib/useMeasure'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {DesktopHeaderContent} from 'components/DesktopHeader/DesktopHeaderContent'
import {SearchProgressBar} from 'components/SearchProgressBar'
import {useTopHeaderHeight} from 'hooks/useTopHeaderHeight'
import {
  getIsTopHeaderHidden,
  getSearchHasBookingHint
} from 'modules/search/selectors'
import {
  setIsTopHeaderHidden,
  setSearchDesktopHeaderHeight
} from 'modules/search/slice'
import {motion, useScroll} from 'motion/react'

import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useIsEmbeddedWithBrand} from '@vio/core/src/_web/brand/hooks/useIsEmbeddedWithBrand'
import {useRouterProvider} from '@vio/core/src/_web/router/context/RouterContext'

interface Props {
  /** Whether to show the search form in the header */
  hasSearchForm?: boolean
  /** Whether to show the filters bar in the header */
  hasFilters?: boolean
  /** Whether the header should be fixed or not */
  isFixed?: boolean
  /** Whether the search progress bar should appear or not */
  hasSearchProgressBar?: boolean
  /** Custom component to override default search box */
  customSearchBox?: React.ReactElement
  /** Whether the user widget should be hidden */
  disableUserWidget?: boolean
  /** Whether the "Get the app" button should be hidden */
  disableGetTheApp?: boolean
}

const HeaderWrapper = styled(motion.div)`
  width: 100%;
  position: relative;
  z-index: 4;

  &.--fixed {
    position: sticky;
    top: 0;
  }
`

export const DesktopHeader = ({
  hasSearchForm,
  hasFilters = false,
  isFixed = true,
  hasSearchProgressBar = false,
  customSearchBox,
  disableUserWidget = false,
  disableGetTheApp = false
}: Props) => {
  const dispatch = useDispatch()
  const {shell} = useBrandCapabilities()
  const isEmbedded = useIsEmbeddedWithBrand()

  const topHeaderIsHidden = useSelector(getIsTopHeaderHidden)

  const [headerRef, {height}] = useMeasure()

  const shouldHideHeaderTopOnScroll =
    shell.hasCollapsingHeader && isFixed && !isEmbedded
  const topHeaderHeight = useTopHeaderHeight()

  const hasBookingHint = useSelector(getSearchHasBookingHint)

  const {scrollY} = useScroll()
  const {pathname} = useRouterProvider()

  useEffect(() => {
    if (shouldHideHeaderTopOnScroll) {
      const unsubscribe = scrollY.on('change', v => {
        if (v > topHeaderHeight) {
          dispatch(setIsTopHeaderHidden(true))
        } else {
          dispatch(setIsTopHeaderHidden(false))
        }
      })
      return () => unsubscribe()
    }
  }, [scrollY, dispatch, shouldHideHeaderTopOnScroll, topHeaderHeight])

  useEffect(() => {
    if (shouldHideHeaderTopOnScroll) {
      dispatch(setIsTopHeaderHidden(false))
    }
  }, [dispatch, pathname, shouldHideHeaderTopOnScroll])

  useEffect(() => {
    dispatch(setSearchDesktopHeaderHeight(height))
  }, [dispatch, height])

  return (
    <>
      <HeaderWrapper
        initial={false}
        animate={{
          y: topHeaderIsHidden ? -topHeaderHeight : 0
        }}
        ref={headerRef}
        className={cx(isFixed && '--fixed')}
      >
        <DesktopHeaderContent
          hasSearchForm={hasSearchForm}
          hasFilters={hasFilters}
          customSearchBox={customSearchBox}
          showDateConfirmationTooltip={hasBookingHint}
          disableUserWidget={disableUserWidget}
          disableGetTheApp={disableGetTheApp}
        />
        {hasSearchProgressBar && <SearchProgressBar />}
      </HeaderWrapper>
    </>
  )
}
